import { useState, useRef, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { ChevronLeft, ChevronRight, Save } from "lucide-react";
import { toast } from "sonner";
import { nanoid } from "nanoid";
import { usePhotoEditor } from "@/hooks/usePhotoEditor";
import { useEditorHistory } from "@/hooks/useEditorHistory";
import { useOrganizationSettings } from "@/hooks/useOrganizationSettings";
import { 
  applyTransformations, 
  drawAnnotations, 
  drawWatermark, 
  drawTimestamp,
  canvasToBlob,
  drawPreview,
  Annotation
} from "@/utils/imageProcessing";
import { EditorToolbar } from "./photo-editor/EditorToolbar";
import { AdjustmentTools } from "./photo-editor/AdjustmentTools";
import { AnnotationTools } from "./photo-editor/AnnotationTools";
import { WatermarkSettings } from "./photo-editor/WatermarkSettings";
import { TimestampOverlay } from "./photo-editor/TimestampOverlay";
import { CategoryTags } from "./photo-editor/CategoryTags";
import { DownloadOptions } from "./photo-editor/DownloadOptions";

interface PinMarker {
  id: string;
  x: number;
  y: number;
  label: string;
  color: string;
  aiPredictionIndex?: number;
}

interface PhotoMarkupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  photo: {
    id: string;
    url: string;
    filename: string;
    ai_damage_types?: string[];
    ai_analysis_result?: any;
    pins?: PinMarker[];
    category?: string;
    created_at?: string;
  } | null;
  onSave: (photo: any) => void;
  allPhotos?: Array<{
    id: string;
    url: string;
    filename: string;
    ai_damage_types?: string[];
    ai_analysis_result?: any;
    pins?: PinMarker[];
  }>;
  onNavigate?: (photoId: string) => void;
}

const DAMAGE_TYPES = [
  { label: "Missing Shingles", color: "#ef4444" },
  { label: "Hail Damage", color: "#f97316" },
  { label: "Wind Damage", color: "#eab308" },
  { label: "Water Damage", color: "#3b82f6" },
  { label: "Cracked Shingles", color: "#8b5cf6" },
  { label: "Flashing Damage", color: "#ec4899" },
  { label: "Granule Loss", color: "#06b6d4" },
  { label: "Other Damage", color: "#6b7280" },
];

export const PhotoMarkupDialog: React.FC<PhotoMarkupDialogProps> = ({
  open,
  onOpenChange,
  photo,
  onSave,
  allPhotos = [],
  onNavigate,
}) => {
  const editor = usePhotoEditor();
  const history = useEditorHistory();
  const { settings: orgSettings } = useOrganizationSettings();

  const [selectedLabel, setSelectedLabel] = useState(DAMAGE_TYPES[0].label);
  const [activeTool, setActiveTool] = useState<string>('select');
  const [placingForAI, setPlacingForAI] = useState<number | null>(null);
  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStartPos, setDrawStartPos] = useState<{ x: number; y: number } | null>(null);
  const [currentDrawPos, setCurrentDrawPos] = useState<{ x: number; y: number } | null>(null);
  const [currentFreehandPoints, setCurrentFreehandPoints] = useState<{ x: number; y: number }[]>([]);
  
  // Annotation settings
  const [annotationColor, setAnnotationColor] = useState('#ef4444');
  const [annotationThickness, setAnnotationThickness] = useState(3);
  
  // Original image data for comparison
  const [originalImageData, setOriginalImageData] = useState<ImageData | null>(null);
  
  // Watermark settings
  const [watermarkEnabled, setWatermarkEnabled] = useState(false);
  const [watermarkPosition, setWatermarkPosition] = useState<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'>('bottom-right');
  const [watermarkOpacity, setWatermarkOpacity] = useState(0.5);
  const [watermarkSize, setWatermarkSize] = useState(100);
  
  // Timestamp settings
  const [timestampEnabled, setTimestampEnabled] = useState(false);
  const [timestampPosition, setTimestampPosition] = useState<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'>('top-left');
  const [timestampFormat, setTimestampFormat] = useState<'date' | 'time' | 'both'>('both');
  
  // Category & tags
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  
  // Compare mode
  const [compareMode, setCompareMode] = useState(false);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const originalCanvasRef = useRef<HTMLCanvasElement>(null);
  const logoImgRef = useRef<HTMLImageElement | null>(null);

  // Load logo
  useEffect(() => {
    if (orgSettings?.logo_url) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = orgSettings.logo_url;
      img.onload = () => {
        logoImgRef.current = img;
        redrawCanvas();
      };
    }
  }, [orgSettings?.logo_url]);

  useEffect(() => {
    if (open && photo) {
      // Load saved data from ai_analysis_result
      const savedData = photo.ai_analysis_result || {};
      editor.loadPhotoData({
        pins: photo.pins || [],
        transform: savedData.transform,
        annotations: savedData.annotations || [],
      });
      setCategory(photo.category || '');
      setTags(savedData.tags || []);
      
      // Save initial snapshot for undo/redo
      history.clearHistory();
      history.saveSnapshot({
        annotations: savedData.annotations || [],
        pins: photo.pins || [],
        transform: savedData.transform || {
          rotation: 0,
          flipHorizontal: false,
          flipVertical: false,
          brightness: 0,
          contrast: 0,
        },
      });
    }
  }, [open, photo]);

  const currentIndex = allPhotos.findIndex(p => p.id === photo?.id);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < allPhotos.length - 1;

  const handlePrevious = useCallback(() => {
    if (hasPrevious && onNavigate && allPhotos[currentIndex - 1]) {
      if (editor.hasUnsavedChanges) {
        if (confirm('You have unsaved changes. Save before navigating?')) {
          handleSave();
        }
      }
      onNavigate(allPhotos[currentIndex - 1].id);
    }
  }, [hasPrevious, onNavigate, allPhotos, currentIndex, editor.hasUnsavedChanges]);

  const handleNext = useCallback(() => {
    if (hasNext && onNavigate && allPhotos[currentIndex + 1]) {
      if (editor.hasUnsavedChanges) {
        if (confirm('You have unsaved changes. Save before navigating?')) {
          handleSave();
        }
      }
      onNavigate(allPhotos[currentIndex + 1].id);
    }
  }, [hasNext, onNavigate, allPhotos, currentIndex, editor.hasUnsavedChanges]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        handleNext();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };
    
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [open, handlePrevious, handleNext]);

  useEffect(() => {
    if (open) {
      window.addEventListener('resize', redrawCanvas);
      return () => window.removeEventListener('resize', redrawCanvas);
    }
  }, [open]);

  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePos(e);
    
    // Save snapshot before action
    history.saveSnapshot({
      annotations: editor.annotations,
      pins: editor.pins,
      transform: editor.transform,
    });
    
    if (activeTool === 'pin') {
      const newPin: PinMarker = {
        id: nanoid(),
        x: pos.x,
        y: pos.y,
        label: selectedLabel,
        color: DAMAGE_TYPES.find(t => t.label === selectedLabel)?.color || '#ef4444',
        aiPredictionIndex: placingForAI !== null ? placingForAI : undefined,
      };
      
      editor.addPin(newPin);
      setActiveTool('select');
      setPlacingForAI(null);
    } else if (activeTool === 'text') {
      const text = prompt('Enter text:');
      if (text) {
        const annotation: Annotation = {
          id: nanoid(),
          type: 'text',
          color: annotationColor,
          data: {
            x: pos.x,
            y: pos.y,
            text,
            fontSize: 20,
          },
        };
        editor.addAnnotation(annotation);
      }
      setActiveTool('select');
    } else if (['arrow', 'rectangle', 'circle'].includes(activeTool)) {
      setIsDrawing(true);
      setDrawStartPos(pos);
      setCurrentDrawPos(pos);
    } else if (activeTool === 'freehand') {
      setIsDrawing(true);
      setCurrentFreehandPoints([pos]);
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    
    const pos = getMousePos(e);
    
    if (activeTool === 'freehand') {
      setCurrentFreehandPoints(prev => [...prev, pos]);
    } else if (['arrow', 'rectangle', 'circle'].includes(activeTool)) {
      setCurrentDrawPos(pos);
    }
  };

  const handleCanvasMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) {
      setIsDrawing(false);
      return;
    }
    
    const pos = getMousePos(e);
    const minDragDistance = 5; // Minimum pixels to prevent accidental tiny shapes
    
    if (activeTool === 'arrow' && drawStartPos) {
      const distance = Math.sqrt(
        Math.pow(pos.x - drawStartPos.x, 2) + Math.pow(pos.y - drawStartPos.y, 2)
      );
      if (distance > minDragDistance) {
        const annotation: Annotation = {
          id: nanoid(),
          type: 'arrow',
          color: annotationColor,
          data: {
            x1: drawStartPos.x,
            y1: drawStartPos.y,
            x2: pos.x,
            y2: pos.y,
            thickness: annotationThickness,
          },
        };
        editor.addAnnotation(annotation);
      }
    } else if (activeTool === 'rectangle' && drawStartPos) {
      const width = Math.abs(pos.x - drawStartPos.x);
      const height = Math.abs(pos.y - drawStartPos.y);
      if (width > minDragDistance && height > minDragDistance) {
        const annotation: Annotation = {
          id: nanoid(),
          type: 'rectangle',
          color: annotationColor,
          data: {
            x: Math.min(drawStartPos.x, pos.x),
            y: Math.min(drawStartPos.y, pos.y),
            width,
            height,
            thickness: annotationThickness,
            filled: false,
          },
        };
        editor.addAnnotation(annotation);
      }
    } else if (activeTool === 'circle' && drawStartPos) {
      const radius = Math.sqrt(
        Math.pow(pos.x - drawStartPos.x, 2) + Math.pow(pos.y - drawStartPos.y, 2)
      );
      if (radius > minDragDistance) {
        const annotation: Annotation = {
          id: nanoid(),
          type: 'circle',
          color: annotationColor,
          data: {
            x: drawStartPos.x,
            y: drawStartPos.y,
            radius,
            thickness: annotationThickness,
            filled: false,
          },
        };
        editor.addAnnotation(annotation);
      }
    } else if (activeTool === 'freehand' && currentFreehandPoints.length > 2) {
      const annotation: Annotation = {
        id: nanoid(),
        type: 'freehand',
        color: annotationColor,
        data: {
          points: currentFreehandPoints,
          thickness: annotationThickness,
        },
      };
      editor.addAnnotation(annotation);
      setCurrentFreehandPoints([]);
    }
    
    setIsDrawing(false);
    setDrawStartPos(null);
    setCurrentDrawPos(null);
    setActiveTool('select');
  };

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Apply transformations
    applyTransformations(canvas, img, editor.transform);
    
    // Draw annotations
    drawAnnotations(canvas, editor.annotations);
    
    // Draw preview if currently drawing
    if (isDrawing && drawStartPos && currentDrawPos && ['arrow', 'rectangle', 'circle'].includes(activeTool)) {
      drawPreview(ctx, canvas, activeTool, drawStartPos, currentDrawPos, annotationColor, annotationThickness);
    }
    
    // Draw temp freehand
    if (isDrawing && activeTool === 'freehand' && currentFreehandPoints.length > 0) {
      ctx.beginPath();
      ctx.strokeStyle = annotationColor;
      ctx.lineWidth = annotationThickness;
      ctx.moveTo(currentFreehandPoints[0].x, currentFreehandPoints[0].y);
      for (let i = 1; i < currentFreehandPoints.length; i++) {
        ctx.lineTo(currentFreehandPoints[i].x, currentFreehandPoints[i].y);
      }
      ctx.stroke();
    }
    
    // Draw pins
    editor.pins.forEach((pin, index) => {
      ctx.beginPath();
      ctx.arc(pin.x, pin.y, 20, 0, 2 * Math.PI);
      ctx.fillStyle = pin.color;
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.stroke();
      
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 16px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText((index + 1).toString(), pin.x, pin.y);
    });
    
    // Draw watermark
    if (watermarkEnabled) {
      drawWatermark(
        canvas,
        logoImgRef.current,
        orgSettings?.name || 'Company',
        watermarkPosition,
        watermarkOpacity,
        watermarkSize
      );
    }
    
    // Draw timestamp
    if (timestampEnabled) {
      const date = photo?.created_at ? new Date(photo.created_at) : new Date();
      drawTimestamp(canvas, date, timestampPosition, timestampFormat);
    }
  }, [editor.transform, editor.annotations, editor.pins, watermarkEnabled, watermarkPosition, watermarkOpacity, watermarkSize, timestampEnabled, timestampPosition, timestampFormat, orgSettings, photo, isDrawing, currentFreehandPoints, annotationColor, annotationThickness, activeTool, drawStartPos, currentDrawPos]);

  const handleImageLoad = () => {
    const img = imageRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    
    // Store original image data for comparison
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(img, 0, 0);
      setOriginalImageData(ctx.getImageData(0, 0, canvas.width, canvas.height));
    }

    redrawCanvas();
  };

  // Redraw when state changes
  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  // Redraw comparison canvases when entering compare mode
  useEffect(() => {
    if (compareMode && originalImageData) {
      const originalCanvas = originalCanvasRef.current;
      const editedCanvas = canvasRef.current;
      
      if (originalCanvas && editedCanvas) {
        // Draw original
        originalCanvas.width = originalImageData.width;
        originalCanvas.height = originalImageData.height;
        const originalCtx = originalCanvas.getContext('2d');
        if (originalCtx) {
          originalCtx.putImageData(originalImageData, 0, 0);
        }
        
        // Edited canvas is already drawn by redrawCanvas
      }
    }
  }, [compareMode, originalImageData]);

  const handleSave = async () => {
    toast.success('Saving photo edits...');
    
    onSave({
      ...photo,
      pins: editor.pins,
      category,
      ai_analysis_result: {
        ...photo?.ai_analysis_result,
        tags,
        annotations: editor.annotations,
        transform: editor.transform,
        watermark: watermarkEnabled ? {
          position: watermarkPosition,
          opacity: watermarkOpacity,
          size: watermarkSize,
        } : null,
        timestamp: timestampEnabled ? {
          position: timestampPosition,
          format: timestampFormat,
        } : null,
      },
    });
    
    editor.clearUnsavedChanges();
    onOpenChange(false);
  };

  const handleSaveAndNext = async () => {
    await handleSave();
    if (hasNext) {
      handleNext();
    }
  };

  const handlePlacePinForAI = (index: number, aiPredictions: any[]) => {
    const aiPrediction = aiPredictions[index];
    if (aiPrediction) {
      const aiLabelMap: Record<string, string> = {
        "hail-damage": "Hail Damage",
        "wind-damage": "Wind Damage",
        "missing-shingles": "Missing Shingles",
        "cracked-shingles": "Cracked Shingles",
        "granule-loss": "Granule Loss",
        "flashing-damage": "Flashing Damage",
        "water-damage": "Water Damage",
        "nail-pops": "Other Damage",
        "nail-pop": "Other Damage",
      };

      const mappedLabel = aiLabelMap[aiPrediction.class] || selectedLabel;
      setSelectedLabel(mappedLabel);
    }

    setPlacingForAI(index);
    setActiveTool('pin');
  };

  const handleDownload = async (includeAnnotations: boolean, quality: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      const blob = await canvasToBlob(canvas, quality);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${photo?.filename || 'photo'}_edited.jpg`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Photo downloaded!');
    } catch (error) {
      toast.error('Failed to download photo');
      console.error(error);
    }
  };

  const handleUndo = () => {
    const snapshot = history.undo();
    if (snapshot) {
      editor.restoreSnapshot(snapshot);
      toast.success('Undone');
    }
  };

  const handleRedo = () => {
    const snapshot = history.redo();
    if (snapshot) {
      editor.restoreSnapshot(snapshot);
      toast.success('Redone');
    }
  };

  if (!photo) return null;

  const aiPredictions = photo.ai_analysis_result?.predictions || [];

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen && editor.hasUnsavedChanges) {
        if (confirm('You have unsaved changes. Discard them?')) {
          onOpenChange(false);
        }
      } else {
        onOpenChange(newOpen);
      }
    }}>
      <DialogContent className="max-w-[95vw] h-[95vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span>{photo.filename}</span>
              {editor.hasUnsavedChanges && (
                <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-700">
                  Unsaved changes
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevious}
                disabled={!hasPrevious}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                {currentIndex + 1} / {allPhotos.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNext}
                disabled={!hasNext}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
              >
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              {hasNext && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveAndNext}
                >
                  Save & Next
                </Button>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <EditorToolbar
          onRotateRight={() => {
            history.saveSnapshot({
              annotations: editor.annotations,
              pins: editor.pins,
              transform: editor.transform,
            });
            editor.rotate(90);
          }}
          onRotateLeft={() => {
            history.saveSnapshot({
              annotations: editor.annotations,
              pins: editor.pins,
              transform: editor.transform,
            });
            editor.rotate(-90);
          }}
          onFlipHorizontal={() => {
            history.saveSnapshot({
              annotations: editor.annotations,
              pins: editor.pins,
              transform: editor.transform,
            });
            editor.flipHorizontal();
          }}
          onFlipVertical={() => {
            history.saveSnapshot({
              annotations: editor.annotations,
              pins: editor.pins,
              transform: editor.transform,
            });
            editor.flipVertical();
          }}
          onSelectTool={setActiveTool}
          activeTool={activeTool}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={history.canUndo}
          canRedo={history.canRedo}
          onAutoEnhance={() => {
            history.saveSnapshot({
              annotations: editor.annotations,
              pins: editor.pins,
              transform: editor.transform,
            });
            editor.autoEnhance();
            toast.success('Auto-enhanced!');
          }}
          onCompareToggle={() => setCompareMode(!compareMode)}
          compareMode={compareMode}
        />

        <div className="flex gap-4 flex-1 overflow-hidden px-6 pb-6">
          {/* Left sidebar */}
          <ScrollArea className="w-80 border rounded-lg p-4">
            <div className="space-y-6">
              {activeTool === 'pin' && (
                <div className="space-y-2">
                  <h3 className="font-medium">Pin Label</h3>
                  <Select value={selectedLabel} onValueChange={setSelectedLabel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAMAGE_TYPES.map((type) => (
                        <SelectItem key={type.label} value={type.label}>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: type.color }} />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <AdjustmentTools
                brightness={editor.transform.brightness}
                contrast={editor.transform.contrast}
                onBrightnessChange={(val) => {
                  history.saveSnapshot({
                    annotations: editor.annotations,
                    pins: editor.pins,
                    transform: editor.transform,
                  });
                  editor.setBrightness(val);
                }}
                onContrastChange={(val) => {
                  history.saveSnapshot({
                    annotations: editor.annotations,
                    pins: editor.pins,
                    transform: editor.transform,
                  });
                  editor.setContrast(val);
                }}
                onReset={() => {
                  history.saveSnapshot({
                    annotations: editor.annotations,
                    pins: editor.pins,
                    transform: editor.transform,
                  });
                  editor.resetTransform();
                  toast.success('Reset to original');
                }}
              />

              <Separator />

              <AnnotationTools
                activeTool={activeTool}
                color={annotationColor}
                onColorChange={setAnnotationColor}
                thickness={annotationThickness}
                onThicknessChange={setAnnotationThickness}
                annotations={editor.annotations}
                onDeleteAnnotation={editor.removeAnnotation}
              />

              <Separator />

              <WatermarkSettings
                enabled={watermarkEnabled}
                onEnabledChange={setWatermarkEnabled}
                position={watermarkPosition}
                onPositionChange={setWatermarkPosition}
                opacity={watermarkOpacity}
                onOpacityChange={setWatermarkOpacity}
                size={watermarkSize}
                onSizeChange={setWatermarkSize}
              />

              <Separator />

              <TimestampOverlay
                enabled={timestampEnabled}
                onEnabledChange={setTimestampEnabled}
                position={timestampPosition}
                onPositionChange={setTimestampPosition}
                format={timestampFormat}
                onFormatChange={setTimestampFormat}
              />

              <Separator />

              <CategoryTags
                category={category}
                onCategoryChange={setCategory}
                tags={tags}
                onTagsChange={setTags}
              />

              <Separator />

              {aiPredictions.length > 0 && (
                <>
                  <div className="space-y-2">
                    <h3 className="font-medium text-sm">AI Detected ({aiPredictions.length})</h3>
                    <div className="space-y-2">
                      {aiPredictions.slice(0, 5).map((pred: any, idx: number) => (
                        <div key={idx} className="text-sm p-2 bg-muted rounded-lg flex justify-between items-center">
                          <div className="flex-1">
                            <div className="font-medium">{pred.class}</div>
                            <div className="text-xs text-muted-foreground">
                              {(pred.confidence * 100).toFixed(0)}%
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handlePlacePinForAI(idx, aiPredictions)}
                          >
                            📍
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              <div className="space-y-2">
                <h3 className="font-medium text-sm">Placed Pins ({editor.pins.length})</h3>
                <div className="space-y-2">
                  {editor.pins.map((pin, idx) => (
                    <div key={pin.id} className="text-sm p-2 bg-muted rounded-lg flex justify-between items-center">
                      <div>
                        <div className="font-medium">#{idx + 1} {pin.label}</div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => editor.removePin(pin.id)}
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <DownloadOptions onDownload={handleDownload} />
            </div>
          </ScrollArea>

          {/* Canvas area */}
          <div className="flex-1 relative overflow-hidden bg-muted/30 rounded-lg flex items-center justify-center p-4">
            {compareMode ? (
              <div className="flex gap-4 w-full h-full items-center justify-center">
                <div className="flex-1 flex flex-col items-center">
                  <div className="text-sm font-medium mb-2">Original</div>
                  <canvas ref={originalCanvasRef} className="max-w-full max-h-[70vh] border rounded shadow-lg" />
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <div className="text-sm font-medium mb-2">Edited</div>
                  <canvas ref={canvasRef} className="max-w-full max-h-[70vh] border rounded shadow-lg" />
                </div>
              </div>
            ) : (
              <div className="relative">
                <img
                  ref={imageRef}
                  src={photo.url}
                  alt={photo.filename}
                  className="max-w-full max-h-[70vh] object-contain hidden"
                  onLoad={handleImageLoad}
                  crossOrigin="anonymous"
                />
                <canvas
                  ref={canvasRef}
                  onMouseDown={handleCanvasMouseDown}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseUp={handleCanvasMouseUp}
                  className="max-w-full max-h-[70vh]"
                  style={{ 
                    cursor: activeTool === 'select' ? 'default' : 'crosshair'
                  }}
                />
                <canvas
                  ref={originalCanvasRef}
                  className="hidden"
                />
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
